import { useCallback, useEffect, useRef, useState } from "react";
import type {
  Conversation,
  ConversationListItem,
  Handoff,
  NormalizedMessage,
} from "@qbanq/shared";
import { api } from "../api/client";
import { MediaItem } from "../components/MediaItem";

interface Detail {
  conversation: Conversation;
  messages: NormalizedMessage[];
  handoffs: Handoff[];
  agent_sessions: Array<{ id: string; agent_function: string; managed_session_id: string }>;
}

interface AgentEvent {
  id: string;
  action: string;
  metadata?: Record<string, unknown>;
  created_at: number;
}

const POLL_MS = 8000;

export function InboxPage() {
  const [list, setList] = useState<ConversationListItem[]>([]);
  const [nextBefore, setNextBefore] = useState<number | undefined>();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [events, setEvents] = useState<AgentEvent[]>([]);
  const [showInspector, setShowInspector] = useState(false);
  const [reply, setReply] = useState("");
  const selectedRef = useRef<string | null>(null);
  selectedRef.current = selected;

  const loadList = useCallback(
    async (opts: { append?: boolean; before?: number } = {}) => {
      const params = new URLSearchParams({ limit: "50" });
      if (search.trim()) params.set("q", search.trim());
      if (opts.before) params.set("before", String(opts.before));
      const { data, meta } = await api.getPage<ConversationListItem[]>(
        `/api/inbox/conversations?${params}`,
      );
      setList((prev) => (opts.append ? [...prev, ...data] : data));
      setNextBefore(meta?.next_before as number | undefined);
      if (!opts.append && !selectedRef.current && data[0]) setSelected(data[0].id);
    },
    [search],
  );

  const loadDetail = useCallback((id: string) => {
    void api.get<Detail>(`/api/inbox/conversations/${id}`).then(setDetail);
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (selected) {
      loadDetail(selected);
      setShowInspector(false);
    }
  }, [selected, loadDetail]);

  // Live updates: poll the list and the open conversation (QWA-061).
  useEffect(() => {
    const t = setInterval(() => {
      void loadList();
      if (selectedRef.current) loadDetail(selectedRef.current);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [loadList, loadDetail]);

  async function sendReply() {
    if (!selected || !reply.trim()) return;
    await api.post(`/api/inbox/conversations/${selected}/reply`, { text: reply.trim() });
    setReply("");
    loadDetail(selected);
  }

  async function toggleHandoff(takeOver: boolean) {
    if (!selected) return;
    await api.post(`/api/inbox/conversations/${selected}/handoff`, { take_over: takeOver });
    loadDetail(selected);
    void loadList();
  }

  async function transitionHandoff(handoffId: string, status: string) {
    await api.post(`/api/inbox/handoffs/${handoffId}/transition`, { status });
    if (selected) loadDetail(selected);
    void loadList();
  }

  function openInspector() {
    if (!selected) return;
    setShowInspector(true);
    void api.get<AgentEvent[]>(`/api/inbox/conversations/${selected}/events`).then(setEvents);
  }

  const openHandoffs = (detail?.handoffs ?? []).filter(
    (h) => h.status === "pending" || h.status === "accepted",
  );

  return (
    <div>
      <h2>Inbox</h2>
      <div className="inbox">
        <div className="card conv-list">
          <input
            placeholder="Search name or number…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ marginBottom: 10 }}
          />
          {list.map((c) => (
            <div
              key={c.id}
              className={`conv-item ${selected === c.id ? "active" : ""}`}
              onClick={() => setSelected(c.id)}
            >
              <div className="row" style={{ justifyContent: "space-between" }}>
                <strong>{c.customer_display_name ?? c.external_conversation_id}</strong>
                <span className={`badge ${c.mode}`}>{c.mode}</span>
              </div>
              <div className="muted" style={{ fontSize: 13 }}>
                {c.last_message_preview?.slice(0, 60) ?? "—"}
              </div>
              {c.unresolved_handoff && (
                <span className="badge" style={{ color: "#f5a623" }}>handoff</span>
              )}
            </div>
          ))}
          {list.length === 0 && <p className="muted">No conversations.</p>}
          {nextBefore && (
            <button
              className="btn secondary"
              style={{ marginTop: 8, width: "100%" }}
              onClick={() => void loadList({ append: true, before: nextBefore })}
            >
              Load more
            </button>
          )}
        </div>

        <div className="card">
          {!detail ? (
            <p className="muted">Select a conversation.</p>
          ) : (
            <>
              <div className="row" style={{ justifyContent: "space-between" }}>
                <div>
                  <strong>{detail.conversation.external_conversation_id}</strong>{" "}
                  <span className={`badge ${detail.conversation.mode}`}>{detail.conversation.mode}</span>
                </div>
                <div className="row">
                  <button className="btn secondary" onClick={openInspector}>
                    Agent decisions
                  </button>
                  {detail.conversation.mode === "ai" ? (
                    <button className="btn secondary" onClick={() => toggleHandoff(true)}>
                      Take over
                    </button>
                  ) : (
                    <button className="btn secondary" onClick={() => toggleHandoff(false)}>
                      Hand back to AI
                    </button>
                  )}
                </div>
              </div>

              {openHandoffs.length > 0 && (
                <div className="card" style={{ background: "var(--panel-2)", margin: "10px 0" }}>
                  {openHandoffs.map((h) => (
                    <div key={h.id} className="row" style={{ justifyContent: "space-between" }}>
                      <span className="muted" style={{ fontSize: 13 }}>
                        Handoff · {h.reason} · {h.status}
                        {h.detail ? ` — ${h.detail}` : ""}
                      </span>
                      <span className="row">
                        {h.status === "pending" && (
                          <button className="btn secondary" onClick={() => transitionHandoff(h.id, "accepted")}>
                            Accept
                          </button>
                        )}
                        <button className="btn" onClick={() => transitionHandoff(h.id, "resolved")}>
                          Resolve
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {showInspector && (
                <div className="card" style={{ background: "var(--panel-2)", margin: "10px 0" }}>
                  <strong>Agent decisions</strong>
                  {events.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No agent events.</div>}
                  {events.map((e) => (
                    <div key={e.id} className="muted" style={{ fontSize: 12 }}>
                      {new Date(e.created_at).toLocaleTimeString()} · {e.action}
                      {e.metadata ? ` · ${JSON.stringify(e.metadata)}` : ""}
                    </div>
                  ))}
                </div>
              )}

              <div className="thread" style={{ marginTop: 12 }}>
                {detail.messages.map((m) => (
                  <div key={m.id} className={`bubble ${m.direction}`}>
                    {(m.media ?? []).map((media, i) => (
                      <div key={i} style={{ marginBottom: 4 }}>
                        <MediaItem media={media} />
                      </div>
                    ))}
                    {(m.text || !m.media?.length) && <div>{m.text ?? `[${m.message_type}]`}</div>}
                    <div className="muted" style={{ fontSize: 11 }}>
                      {m.direction} · {m.status} · {new Date(m.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
              </div>

              <div className="row" style={{ marginTop: 12 }}>
                <input
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Type a manual reply…"
                  onKeyDown={(e) => e.key === "Enter" && sendReply()}
                />
                <button className="btn" onClick={sendReply}>Send</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
